/**
 * Basics step body for CreateObjectTypeWizard — display name, plural name,
 * and icon. Extracted from CreateObjectTypeWizard.tsx to keep that file
 * within the project's file-size convention.
 */

import { memo } from 'react';

import { FormField } from '@/lib/base/forms/form-field';
import { InputBase } from '@/lib/base/forms/input/input';
import { InputGroup } from '@/lib/base/forms/input/input-group';
import { useWizardContext } from '@/lib/base/wizard';
import { IconSelect } from '@/lib/crm/utils/icons/IconSelect';

export interface BasicsStepProps {
  displayName: string;
  pluralName: string;
  icon: string | null;
  onDisplayNameChange: (value: string) => void;
  onDisplayNameBlur: () => void;
  onPluralNameChange: (value: string) => void;
  onIconChange: (value: string | null) => void;
}

function BasicsStepBodyImpl({
  displayName,
  pluralName,
  icon,
  onDisplayNameChange,
  onDisplayNameBlur,
  onPluralNameChange,
  onIconChange,
}: BasicsStepProps) {
  const { isSubmitting } = useWizardContext();
  return (
    <div className='space-y-4'>
      <FormField name='displayName' label='Name' isRequired>
        {({ isInvalid, showError, inputId }) => (
          <div onBlur={onDisplayNameBlur}>
            <InputGroup
              id={inputId}
              aria-label='Display name'
              value={displayName}
              onChange={onDisplayNameChange}
              isDisabled={isSubmitting}
              isInvalid={isInvalid}
              showError={showError}
            >
              <InputBase placeholder='e.g. Project' />
            </InputGroup>
          </div>
        )}
      </FormField>

      <FormField name='pluralName' label='Plural name' isRequired>
        {({ isInvalid, showError, inputId }) => (
          <InputGroup
            id={inputId}
            aria-label='Plural name'
            value={pluralName}
            onChange={onPluralNameChange}
            isDisabled={isSubmitting}
            isInvalid={isInvalid}
            showError={showError}
          >
            <InputBase placeholder='e.g. Projects' />
          </InputGroup>
        )}
      </FormField>

      <FormField name='icon'>
        <div className='relative'>
          <IconSelect
            label='Icon'
            value={icon}
            onChange={onIconChange}
            placeholder='Select an icon'
            isDisabled={isSubmitting}
          />
        </div>
      </FormField>
    </div>
  );
}

export const BasicsStepBody = memo(BasicsStepBodyImpl);
